import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { constStyles } from "../../../constants/loyaltyConstant";
import EmptyState from "../EmptyState/EmptyState";
import customConfig from "../../../config";

/**
 * DataTable Component
 * Configuration-driven table with:
 * - Dynamic headers from config array
 * - Consistent styling and layout
 * - Sorting support
 * - Custom cell rendering headerBackgroundColor = '#F5F5F5',
 */

const DataTable = ({
  columns = [], // Array of { key, label, align, sortable, width, render }
  data = [],
  onSort = null,
  orderBy = null,
  order = "asc",
  rowKey = "id",
  striped = true,
  hoverEffect = true,
  headerBackgroundColor = constStyles["constColours"]["disabled"]["light"],
  rowHeight = "auto",
  minWidth = "100%",
  maxHeight = null, // e.g., '600px' - enables sticky header with scrollable body
  stickyHeader = false,
  stickyHeaderOffset = 0,
  sx = {},
  tableContainerSx = {},
  onRowClick = null,
  msgForDisplay = customConfig?.noDataMsg,
  expandedRowKey = null,
  renderExpandedRow = null,
}) => {
  const fontW700 = constStyles["fontW"]["fontWeight700"];
  const isStickyHeaderEnabled = stickyHeader || !!maxHeight;

  const handleHeaderClick = (column) => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  const getSortIcon = (columnKey) => {
    if (!columnKey || columnKey !== orderBy) {
      return "↕";
    }
    return order === "asc" ? "↑" : "↓";
  };

  const getSortIconColor = (columnKey) => {
    if (!columnKey || columnKey !== orderBy) {
      return constStyles["constColours"]["disabled"]["main"];
    }
    return constStyles["constColours"]["warning"]["main"];
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "visible",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { height: 10 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.25)",
          borderRadius: 5,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0,0,0,0.05)",
        },
        ...sx,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          position: "relative",
          zIndex: 1,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: constStyles["borderRd"]["borderRd4px"],
          minWidth,
          width: "max-content",
          overflowX: "visible",
          overflowY: maxHeight ? "auto" : "visible",
          maxHeight: maxHeight || {
            xs: "calc(100dvh - 530px)",
            sm: "calc(100dvh - 390px)",
            md: "calc(100dvh - 330px)",
            lg: "calc(100dvh - 330px)",
          },
          // maxHeight: maxHeight || "none",
          ...tableContainerSx,
        }}
      >
        <Table stickyHeader={isStickyHeaderEnabled}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{
                    backgroundColor: headerBackgroundColor,
                    fontWeight: fontW700,
                    fontSize: constStyles["fontSz"]["fontSz24"],
                    padding: "12px 16px",
                    width: column.width,
                    minWidth: column?.minWidth,
                    whiteSpace: "nowrap",
                    cursor: column.sortable ? "pointer" : "default",
                    userSelect: "none",
                    transition: "background-color 0.2s ease",
                    ...(isStickyHeaderEnabled && {
                      position: "sticky",
                      top: stickyHeaderOffset,
                      zIndex: 2,
                      backgroundColor: headerBackgroundColor,
                    }),
                    "&:hover": {
                      backgroundColor: column.sortable
                        ? constStyles["constColours"]["disabled"]["bg"]
                        : headerBackgroundColor,
                    },
                    ...column.headerCellSx,
                  }}
                  onClick={() => handleHeaderClick(column)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: column.justifyContent || "flex-start",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: fontW700,
                        fontSize: constStyles["fontSz"]["fontSz16"],
                        whiteSpace: "nowrap",
                        ...column.headerLabelSx,
                      }}
                    >
                      {column.label}
                    </Typography>
                    {column.sortable && (
                      <Typography
                        sx={{
                          fontWeight: fontW700,
                          fontSize: constStyles["fontSz"]["fontSz18"],
                          color: getSortIconColor(column.key),
                          transition: "color 0.2s",
                        }}
                      >
                        {getSortIcon(column.key)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ padding: "32px 16px", color: "#757575" }}
                >
                  <EmptyState message={msgForDisplay} />
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row, index) => {
                const resolvedRowKey =
                  row[rowKey] != null ? row[rowKey] : index;
                const isExpanded =
                  expandedRowKey != null && resolvedRowKey === expandedRowKey;

                return (
                  <React.Fragment key={resolvedRowKey}>
                    <TableRow
                      sx={{
                        backgroundColor:
                          striped && index % 2 === 0 ? "white" : "#FAFAFA",
                        ...(hoverEffect && {
                          "&:hover": {
                            backgroundColor:
                              striped && index % 2 === 0
                                ? "#F5F5F5"
                                : "#F5F5F5",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1) inset",
                          },
                        }),
                        transition: "all 0.2s ease",
                        height: rowHeight,
                        cursor: onRowClick ? "pointer" : "default",
                      }}
                      onClick={() => onRowClick && onRowClick(row)}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${resolvedRowKey}-${column.key}`}
                          align={column.align || "left"}
                          sx={{
                            padding: "8px 16px",
                            fontSize: constStyles["fontSz"]["fontSz14"],
                            verticalAlign:
                              column.key === "config" ? "top" : "middle",
                            ...column.cellSx,
                          }}
                        >
                          {column.render
                            ? column.render(row[column.key], row)
                            : row[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && renderExpandedRow && (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ padding: 0 }}>
                          {renderExpandedRow(row)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataTable;
